import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Swiper from 'react-id-swiper';

import Loader from '../Loader';
import PenSlide from './PenSlide';
import CategoriesOverlay from './CategoriesOverlay';
import BadgePopup from './BadgePopup';
import LocaleText from '../LocaleText';
import { getText, hasText } from '../../helpers/translationHelper';
import NoticePopup from '../Configurator/Popups/NoticePopup';

import history from '../../history';
import settings from '../../settings';

class Overview extends React.Component {
    constructor() {
        super();

        this.state = { popupExhibit: true };

        this.swiperProps = { ...settings.swiper.baseSettings };
        this.fetchPensAndBadges = this.fetchPensAndBadges.bind(this);
        this.renderBadgePopup = this.renderBadgePopup.bind(this);
        this.getBadgesForSlide = this.getBadgesForSlide.bind(this);
        this.addEventListenersToSlideDuplicates = this.addEventListenersToSlideDuplicates.bind(
            this
        );
    }

    componentDidMount() {
        const { locale } = this.props;
        if (this.props.pens.length === 0 || locale !== this.props.currentDisplayLocale) {
            this.props.setCurrentDisplayLocale(locale);
            this.fetchPensAndBadges();
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            !_.isEqual(nextState.popupExhibit, this.state.popupExhibit) ||
            !_.isEqual(nextProps.pens, this.props.pens) ||
            !_.isEqual(nextProps.badges, this.props.badges) ||
            !_.isEqual(nextProps.isLoading, this.props.isLoading) ||
            !_.isEqual(nextProps.categoryId, this.props.categoryId) ||
            !_.isEqual(nextProps.activeBadges, this.props.activeBadges) ||
            !_.isEqual(nextProps.locale, this.props.currentDisplayLocale) ||
            !_.isEqual(nextProps.hasOpenedCategories, this.props.hasOpenedCategories)
        );
    }

    componentWillReceiveProps(nextProps) {
        const { locale, isLoading, categoryId } = nextProps;

        if (isLoading) {
            return;
        }

        if (locale !== this.props.currentDisplayLocale) {
            this.props.setCurrentDisplayLocale(locale);
            this.fetchPensAndBadges();
        }

        this.swiperProps.rebuildOnUpdate = categoryId !== this.props.categoryId;
    }

    onCategoryButtonClick() {
        window.overviewActiveSlide = this.swiper.activeIndex;
        this.props.openCategories();
    }

    fetchPensAndBadges() {
        const { fetchPens, fetchBadges, setIsLoading } = this.props;
        setIsLoading(true);
        Promise.all([fetchPens(), fetchBadges()]).then(() => setIsLoading(false));
    }

    componentWillUnmount() {
        this.props.changeCategory(null);
    }

    componentDidUpdate() {
        this.addEventListenersToSlideDuplicates();
    }

    addEventListenersToSlideDuplicates() {
        const duplicateSlides = Array.from(document.querySelectorAll('.swiper-slide-duplicate'));
        const { pens, locale } = this.props;

        duplicateSlides.forEach(slide => {
            const slug = slide.getAttribute('data-slug');
            const buttons = Array.from(slide.querySelectorAll('button'));
            const badges = Array.from(slide.querySelectorAll('.pen-badge'));
            const pen = pens.find(pen => pen.slug === slug);
            const penUrl = `/${locale}/${pen.static === true ? 'static-pen' : 'pen'}/${slug}`;

            buttons.forEach(button => {
                button.addEventListener('click', e => {
                    history.push(penUrl);
                });
            });

            badges.forEach(badge => {
                badge.addEventListener('click', e => {
                    if (badge.hasAttribute('data-badges')) {
                        const badges = badge.getAttribute('data-badges');
                        const badgeIds = badges.split(',').map(id => parseInt(id, 10));
                        this.props.setActiveBadges(badgeIds);
                    }
                });
            });
        });
    }

    renderBadgePopup() {
        const { activeBadges, setActiveBadges, badges, isLoading } = this.props;

        if (activeBadges === null || isLoading) {
            return null;
        }

        const badgeObjects = activeBadges.map(badgeId =>
            badges.find(badge => badge.id === badgeId)
        );

        return (
            <BadgePopup badges={badgeObjects} isOpen={true} toggle={() => setActiveBadges(null)} />
        );
    }

    getBadgesForSlide(ids) {
        const badges = [];
        ids.forEach(id => {
            const badge = this.props.badges.find(badge => badge.id === id);
            if (badge) {
                const { name, image, id, type } = badge;
                badges.push({ name, image, id, type });
            }
        });
        return badges;
    }

    togglePopupExhibit() {
        this.setState(state => {
            return {
                popupExhibit: !state.popupExhibit,
            };
        });
    }

    render() {
        const {
            pens,
            recentPen,
            categories,
            categoryId,
            isLoading,
            setActiveBadges,
            locale,
        } = this.props;

        // return early if component is still loading
        if (isLoading) {
            return (
                <div className="overview">
                    <Loader />
                </div>
            );
        }

        this.swiperProps.activeSlideKey = this.swiper ? this.swiper.activeIndex : null;
        this.swiperProps.initialSlide = recentPen ? pens.findIndex(o => o.slug === recentPen) : 0;

        const allCategoriesName = <LocaleText textKey="overview.categories.title" />;
        const currentCategory = categories.filter(category => category.id === categoryId)[0];
        const currentCategoryName = currentCategory ? currentCategory.name : allCategoriesName;
        const filteredPens = categoryId ? pens.filter(pen => pen.category.id === categoryId) : pens;

        const configuredPenSlugs = [];
        for (let i = 0, len = localStorage.length; i < len; ++i) {
            configuredPenSlugs.push(localStorage.key(i));
        }

        return (
            <div className="overview">
                <div className="filter-wrapper">
                    <button
                        onClick={this.onCategoryButtonClick.bind(this)}
                        className="filter-select-btn"
                    >
                        {currentCategoryName}
                    </button>
                </div>

                {filteredPens.length > 0 ? (
                    <Swiper
                        {...this.swiperProps}
                        ref={node => (node ? (this.swiper = node.swiper) : undefined)}
                    >
                        {filteredPens.map(pen => (
                            <PenSlide
                                key={`pen-slide-${pen.slug}`}
                                pen={pen}
                                badges={this.getBadgesForSlide(pen.badges)}
                                lang={locale}
                                openBadgePopup={setActiveBadges}
                                touched={configuredPenSlugs.includes(pen.slug)}
                            />
                        ))}
                    </Swiper>
                ) : null}

                <CategoriesOverlay
                    isOpened={this.props.hasOpenedCategories}
                    close={this.props.closeCategories}
                    changeCategory={this.props.changeCategory}
                    allCategoriesName={allCategoriesName}
                    categories={this.props.categories}
                    currentCategory={this.props.categoryId}
                />

                {this.renderBadgePopup()}
            </div>
        );
    }
}

Overview.propTypes = {
    closeCategories: PropTypes.func.isRequired,
    changeCategory: PropTypes.func.isRequired,
    openCategories: PropTypes.func.isRequired,
    fetchBadges: PropTypes.func.isRequired,
    fetchPens: PropTypes.func.isRequired,
    setActiveBadges: PropTypes.func.isRequired,
    setCurrentDisplayLocale: PropTypes.func.isRequired,
    setIsLoading: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    activeBadges: PropTypes.array,
    badges: PropTypes.array,
    pens: PropTypes.array,
    categories: PropTypes.array,
    currentDisplayLocale: PropTypes.string,
    hasOpenedCategories: PropTypes.bool,
    isLoading: PropTypes.bool,
    locale: PropTypes.string,
};

Overview.defaultProps = {
    activeBadges: null,
    badges: [],
    pens: [],
    categories: [],
    currentDisplayLocale: null,
    hasOpenedCategories: false,
    isLoading: true,
    locale: 'de',
};

export default Overview;
